import { FormInputBlock } from './form-input-block.js'
import type { EventType, HtmlType } from '../../types.js'
import type { TsCodeType } from '../../types.js'
import {
	jsonClass,
	jsonProperty,
	jsonArrayProperty,
} from '@sumbricht/json-class-serializer'
import { ScreenEditorInput } from '../decorators/screen-editor-input.js'
import { Expression } from '../../data/text-or-code.js'

export const SliderBlockTypeNames = {
	single: 'Single value',
	range: 'Range (from, to)',
} as const
export type SliderBlockType = keyof typeof SliderBlockTypeNames

@jsonClass('SliderBlock')
export class SliderBlock extends FormInputBlock {
	@jsonProperty(String)
	@ScreenEditorInput({
		inputType: 'dropdown',
		order: 1,
		label: 'Type',
		options: Object.entries(SliderBlockTypeNames),
		isBinding: false,
	})
	type: SliderBlockType = 'single'

	@jsonProperty(Expression)
	@ScreenEditorInput<SliderBlock>({
		inputType: 'code',
		order: 2,
		label: (blocks) => (blocks[0].type == 'range' ? 'Binding from' : 'Binding'),
		codeLanguage: 'ts',
		tsReturnType: () => 'number',
		editorSize: 'singleline',
		isBinding: true,
		isMandatory: () => true,
	})
	bindingFromCode = new Expression('')

	@jsonProperty(Expression)
	@ScreenEditorInput<SliderBlock>({
		inputType: 'code',
		order: 3,
		label: 'Binding to',
		codeLanguage: 'ts',
		tsReturnType: () => 'number',
		editorSize: 'singleline',
		isBinding: true,
		isVisible: (blocks) => blocks[0].type == 'range',
		isMandatory: () => true,
	})
	bindingToCode = new Expression('')

	@jsonProperty(Expression)
	@ScreenEditorInput<SliderBlock>({
		inputType: 'code',
		order: 4,
		label: 'Minimum value',
		codeLanguage: 'ts',
		tsReturnType: () => 'number',
		editorSize: 'singleline',
		isBinding: false,
		isMandatory: () => true,
	})
	minimumValueCode = new Expression('1')

	@jsonProperty(Expression)
	@ScreenEditorInput<SliderBlock>({
		inputType: 'code',
		order: 5,
		label: 'Maximum value',
		codeLanguage: 'ts',
		tsReturnType: () => 'number',
		editorSize: 'singleline',
		isBinding: false,
		isMandatory: () => true,
	})
	maximumValueCode = new Expression('10')

	@jsonProperty(Expression)
	@ScreenEditorInput<SliderBlock>({
		inputType: 'code',
		order: 6,
		label: 'Step size',
		codeLanguage: 'ts',
		tsReturnType: () => 'number',
		editorSize: 'singleline',
		isBinding: false,
		isMandatory: () => true,
	})
	stepSizeCode = new Expression('1')

	@jsonProperty(Boolean)
	@ScreenEditorInput<SliderBlock>({
		inputType: 'checkbox',
		order: 7,
		label: 'Show tick marks',
	})
	showTicks: boolean = true

	constructor(init: Partial<SliderBlock> = {}) {
		super()
		this.exampleText = 'Some input'
		this.init(init)
	}

	get allowedEventTypes(): EventType[] {
		return [{ name: 'change' }]
	}

	getValidationValueType(): string {
		return 'number'
	}

	producePreview(): HtmlType {
		return `<mat-slider><input matSliderThumb></mat-slider>`
	}

	getEditorCategory(): string {
		return 'Additional Form Fields'
	}
}
